<template>
  <div class="video-filters-panel">
    <div class="video-filters-header d-flex align-center justify-space-between px-4 py-2">
      <span class="text-subtitle-2 font-weight-bold">Efectos de video</span>
      <v-btn icon x-small @click="$emit('close')">
        <Icon :size="18" color="#666" icon="close" />
      </v-btn>
    </div>

    <div class="video-filters-body px-4 pb-3">
      <!-- Background Section -->
      <div
        v-if="support.backgroundBlur && backgroundsEnabled"
        class="filter-section mb-3"
      >
        <div class="text-caption font-weight-medium grey--text text--darken-1 mb-2">FONDO</div>
        <div class="d-flex flex-wrap" style="gap: 6px">
          <v-btn
            x-small
            depressed
            :color="activeBackground === null ? btnColor : ''"
            :dark="activeBackground === null"
            @click="disableBackground()"
            class="text-none filter-chip"
          >
            Ninguno
          </v-btn>
          <v-btn
            x-small
            depressed
            :color="activeBackground === 'blur-low' ? btnColor : ''"
            :dark="activeBackground === 'blur-low'"
            @click="setBlur('low')"
            class="text-none filter-chip"
          >
            Blur suave
          </v-btn>
          <v-btn
            x-small
            depressed
            :color="activeBackground === 'blur-high' ? btnColor : ''"
            :dark="activeBackground === 'blur-high'"
            @click="setBlur('high')"
            class="text-none filter-chip"
          >
            Blur fuerte
          </v-btn>
        </div>
        <div class="text-caption grey--text mt-2 mb-1" style="font-size: 10px">Fondos de color</div>
        <div class="d-flex flex-wrap" style="gap: 6px">
          <button
            v-for="preset in presetBackgrounds"
            :key="preset.id"
            class="preset-swatch"
            :class="{ 'preset-swatch--active': activeBackground === 'preset-' + preset.id }"
            :style="{ background: preset.css }"
            :title="preset.label"
            @click="setPresetBackground(preset)"
          />
        </div>
        <div class="mt-2 d-flex align-center" style="gap: 6px">
          <v-text-field
            v-model="bgImageUrl"
            dense
            hide-details
            outlined
            placeholder="URL de imagen de fondo"
            class="caption-input"
            style="font-size: 11px"
          />
          <v-btn
            x-small
            depressed
            :color="btnColor"
            dark
            @click="setVirtualBg()"
            :disabled="!bgImageUrl"
            class="text-none"
            style="min-width: 50px"
          >
            Aplicar
          </v-btn>
        </div>
      </div>

      <div
        v-else-if="backgroundsEnabled"
        class="filter-section mb-3"
      >
        <div class="text-caption font-weight-medium grey--text text--darken-1 mb-1">FONDO</div>
        <div class="text-caption grey--text">
          No disponible en {{ support.browser.name }}
        </div>
      </div>

      <!-- NSFW Detection Section -->
      <div class="filter-section">
        <div class="d-flex align-center justify-space-between">
          <div>
            <div class="text-caption font-weight-medium grey--text text--darken-1">DETECCIÓN NSFW</div>
            <div class="text-caption grey--text" style="font-size: 10px; line-height: 1.2">
              Detecta contenido explícito en video
            </div>
          </div>
          <v-switch
            v-model="nsfwActive"
            dense
            hide-details
            :color="btnColor"
            class="mt-0 pt-0"
            @change="toggleNSFW()"
          />
        </div>
        <div
          v-if="nsfwLoading"
          class="text-caption grey--text mt-1"
        >
          Cargando modelo...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'

function generateSolidColorDataUrl (hex, size = 64) {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = hex
  ctx.fillRect(0, 0, size, size)
  return canvas.toDataURL('image/png')
}

@Component({
  components: {
    Icon: () => import(/* webpackPrefetch: true */ '@/components/Icon.vue')
  }
})
export default class VideoFilters extends Vue {
  @Prop({ default: null }) videoProcessor

  nsfwModelReadyHandler = null
  nsfwDetectedHandler = null

  presetBackgrounds = [
    { id: 'white', label: 'Blanco', color: '#FFFFFF', css: '#FFFFFF' },
    { id: 'black', label: 'Negro', color: '#000000', css: '#000000' },
    { id: 'gray', label: 'Gris', color: '#9E9E9E', css: '#9E9E9E' },
    { id: 'blue', label: 'Azul', color: '#1565C0', css: '#1565C0' },
    { id: 'navy', label: 'Azul oscuro', color: '#0D47A1', css: '#0D47A1' },
    { id: 'teal', label: 'Verde azulado', color: '#00897B', css: '#00897B' },
    { id: 'green', label: 'Verde', color: '#2E7D32', css: '#2E7D32' },
    { id: 'red', label: 'Rojo', color: '#C62828', css: '#C62828' },
    { id: 'purple', label: 'Morado', color: '#6A1B9A', css: '#6A1B9A' },
    { id: 'orange', label: 'Naranja', color: '#E65100', css: '#E65100' }
  ]

  data () {
    const vpConfig = this.$organization.videoProcessorConfig()

    return {
      support: {
        backgroundBlur: false,
        browser: { name: 'unknown' }
      },
      activeBackground: null,
      bgImageUrl: '',
      nsfwActive: false,
      nsfwLoading: false,
      backgroundsEnabled: vpConfig.data.backgrounds?.enabled === true,
      nsfwAutoEnabled: vpConfig.isActive && vpConfig.data.nsfw?.autoEnable === true
    }
  }

  get btnColor () {
    const config = this.$organization.btnPrimaryConfig()
    return config && config.data ? config.data.btn : 'primary'
  }

  mounted () {
    if (this.videoProcessor) {
      this.support = this.videoProcessor.isSupported()
      this.syncState()
    }
  }

  @Watch('videoProcessor')
  onProcessorChange () {
    if (this.videoProcessor) {
      this.support = this.videoProcessor.isSupported()
      this.syncState()
    }
  }

  syncState () {
    if (!this.videoProcessor) return

    const filters = this.videoProcessor.getActiveFilters()

    for (const f of filters) {
      if (f.type === 'blur') {
        this.activeBackground = f.config.radius === 'low' ? 'blur-low' : 'blur-high'
      } else if (f.type === 'virtual') {
        this.activeBackground = 'virtual'
      } else if (f.type === 'video') {
        this.activeBackground = 'video'
      } else if (f.type === 'nsfw') {
        this.nsfwActive = true
      }
    }

    if (this.$store.state.videoProcessor.nsfwDetection) {
      this.nsfwActive = true
    }
  }

  async setBlur (level) {
    if (!this.videoProcessor) return
    try {
      await this.videoProcessor.enableBackgroundBlur({ radius: level })
      this.activeBackground = `blur-${level}`
      this.$store.commit('SET_VIDEO_PROCESSOR_BACKGROUND', `blur-${level}`)
    } catch (e) {
      console.error('[VideoFilters] Blur error:', e)
    }
  }

  async setVirtualBg () {
    if (!this.videoProcessor || !this.bgImageUrl) return
    try {
      await this.videoProcessor.setVirtualBackground({ imageUrl: this.bgImageUrl })
      this.activeBackground = 'virtual'
      this.$store.commit('SET_VIDEO_PROCESSOR_BACKGROUND', 'virtual')
    } catch (e) {
      console.error('[VideoFilters] Virtual bg error:', e)
    }
  }

  async setPresetBackground (preset) {
    if (!this.videoProcessor) return
    try {
      const imageUrl = generateSolidColorDataUrl(preset.color)
      await this.videoProcessor.setVirtualBackground({ imageUrl })
      this.activeBackground = `preset-${preset.id}`
      this.$store.commit('SET_VIDEO_PROCESSOR_BACKGROUND', `preset-${preset.id}`)
    } catch (e) {
      console.error('[VideoFilters] Preset bg error:', e)
    }
  }

  async disableBackground () {
    if (!this.videoProcessor) return
    try {
      await this.videoProcessor.disableBackground()
      this.activeBackground = null
      this.$store.commit('SET_VIDEO_PROCESSOR_BACKGROUND', null)
    } catch (e) {
      console.error('[VideoFilters] Disable bg error:', e)
    }
  }

  removeNSFWListeners () {
    if (!this.videoProcessor) return
    if (this.nsfwModelReadyHandler) {
      this.videoProcessor.off('nsfw:model:ready', this.nsfwModelReadyHandler)
      this.nsfwModelReadyHandler = null
    }
    if (this.nsfwDetectedHandler) {
      this.videoProcessor.off('nsfw:detected', this.nsfwDetectedHandler)
      this.nsfwDetectedHandler = null
    }
  }

  async toggleNSFW () {
    if (!this.videoProcessor) return
    try {
      if (this.nsfwActive) {
        this.nsfwLoading = true

        this.removeNSFWListeners()

        this.nsfwModelReadyHandler = () => {
          this.nsfwLoading = false
        }
        this.nsfwDetectedHandler = (data) => {
          console.log('nsfw:detected', data)
          this.$store.commit('SET_VIDEO_PROCESSOR_NSFW_EVENT', data)
        }

        this.videoProcessor.on('nsfw:model:ready', this.nsfwModelReadyHandler)
        this.videoProcessor.on('nsfw:detected', this.nsfwDetectedHandler)

        await this.videoProcessor.enableNSFWDetection({
          sampleIntervalMs: 1500,
          threshold: 0.7,
          model: 'MobileNetV2',
          categories: ['Porn', 'Hentai']
        })

        this.$store.commit('SET_VIDEO_PROCESSOR_NSFW', true)
      } else {
        this.removeNSFWListeners()
        await this.videoProcessor.disableNSFWDetection()
        this.nsfwLoading = false
        this.$store.commit('SET_VIDEO_PROCESSOR_NSFW', false)
        this.$store.commit('SET_VIDEO_PROCESSOR_NSFW_EVENT', null)
      }
    } catch (e) {
      console.error('[VideoFilters] NSFW error:', e)
      this.nsfwActive = false
      this.nsfwLoading = false
    }
  }

  beforeDestroy () {
    this.removeNSFWListeners()
  }
}
</script>

<style scoped>
.video-filters-panel {
  background: white;
  border-top: 1px solid #e0e0e0;
  max-height: 380px;
  overflow-y: auto;
}

.video-filters-header {
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.filter-section {
  padding-top: 4px;
}

.filter-section + .filter-section {
  border-top: 1px solid #f5f5f5;
  padding-top: 8px;
}

.filter-chip {
  font-size: 11px !important;
  letter-spacing: normal !important;
  height: 26px !important;
}

.preset-swatch {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #e0e0e0;
  cursor: pointer;
  transition: transform 0.15s, border-color 0.15s;
  padding: 0;
  outline: none;
  flex-shrink: 0;
}

.preset-swatch:hover {
  transform: scale(1.15);
  border-color: #999;
}

.preset-swatch--active {
  border-color: #1565C0;
  box-shadow: 0 0 0 2px rgba(21, 101, 192, 0.35);
  transform: scale(1.1);
}

.caption-input >>> .v-input__slot {
  min-height: 28px !important;
}

.caption-input >>> input {
  font-size: 11px !important;
  padding: 2px 0 !important;
}

.video-filters-panel >>> .v-input--switch {
  transform: scale(0.8);
  transform-origin: right center;
}

</style>
